import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  DepartmentSkillFrameworkData,
  RetiredSkillFrameworkVersion,
  RetirementPlan,
  SkillDetail,
  SkillFramework,
  SkillFrameworkNodesData,
  SkillFrameworkSelectionRequest,
  SkillFrameworkType,
  SelectedSkill,
  SkillSearchData,
  SkillSearchParams,
  SkillsConfiguration,
  SkillsConfigurationRequest,
} from '../../entities/skills-framework.model';
import {
  GroupUpdatesResponse,
  SaveCustomFrameworkPayload,
} from '../../entities/custom-skills-framework.model';
import { DashboardApiService } from '../db-api.service';

const FRAMEWORKS_ENDPOINT = '/v1/issuer/skill_frameworks';
const SELECTIONS_ENDPOINT = '/v1/issuer/skill_framework_selections';
const SKILLS_CONFIGURATION_ENDPOINT = '/v1/issuer/skills_configuration';
const SKILLS_ENDPOINT = '/v1/issuer/skills';

@Injectable({
  providedIn: 'root',
})
export class SkillFrameworksApiService extends DashboardApiService {
  protected override _getHeaders(method: string, endpoint: string): { [header: string]: string } {
    const headers = super._getHeaders(method, endpoint);
    return {
      ...headers,
      'accredible-department-id': this.embedding.departmentId.toString(),
    };
  }

  getFramework(id: number, skillFrameworkVersionId?: number): Observable<SkillFramework> {
    const query = this._buildQueryString({ skill_framework_version_id: skillFrameworkVersionId });
    const base = `${FRAMEWORKS_ENDPOINT}/${id}`;
    const endpoint = query ? `${base}?${query}` : base;

    return this._get(endpoint).pipe(
      map((res) => this._handleResponse(res, 'skill_framework')),
      catchError((res) => this._handleError(res)),
    );
  }

  deleteFramework(id: number): Observable<void> {
    return this._delete(`${FRAMEWORKS_ENDPOINT}/${id}`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  retireFramework(id: number): Observable<RetiredSkillFrameworkVersion> {
    return this._post(`${FRAMEWORKS_ENDPOINT}/${id}/retire`, {}).pipe(
      map((res) => this._handleResponse(res, 'skill_framework_version')),
      catchError((res) => this._handleError(res)),
    );
  }

  getRetirementPlan(id: number): Observable<RetirementPlan> {
    return this._get(`${FRAMEWORKS_ENDPOINT}/${id}/retirement_plan`).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  saveCustomFramework(id: number, payload: SaveCustomFrameworkPayload): Observable<SkillFramework> {
    return this._put(`${FRAMEWORKS_ENDPOINT}/${id}`, payload).pipe(
      map((res) => this._handleResponse(res, 'skill_framework')),
      catchError((res) => this._handleError(res)),
    );
  }

  applyGroupUpdates(id: number, fromVersionId: number): Observable<GroupUpdatesResponse> {
    return this._post(`${FRAMEWORKS_ENDPOINT}/${id}/group_updates`, {
      from_version_id: fromVersionId,
    }).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  listFrameworks(type?: SkillFrameworkType): Observable<SkillFramework[]> {
    const query = this._buildQueryString({ type });
    const endpoint = query ? `${FRAMEWORKS_ENDPOINT}?${query}` : FRAMEWORKS_ENDPOINT;

    return this._get(endpoint).pipe(
      map((res) => this._handleResponse(res, 'skill_frameworks')),
      catchError((res) => this._handleError(res)),
    );
  }

  listFrameworkSelections(): Observable<DepartmentSkillFrameworkData> {
    return this._get(SELECTIONS_ENDPOINT).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  createFrameworkSelection(request: SkillFrameworkSelectionRequest): Observable<SkillFramework> {
    return this._post(SELECTIONS_ENDPOINT, { skill_framework_selection: request }).pipe(
      map((res) => this._handleResponse(res, 'skill_framework')),
      catchError((res) => this._handleError(res)),
    );
  }

  getConfiguration(): Observable<SkillsConfiguration> {
    return this._get(SKILLS_CONFIGURATION_ENDPOINT).pipe(
      map((res) => this._handleResponse(res, 'skills_configuration')),
      catchError((res) => this._handleError(res)),
    );
  }

  updateConfiguration(request: SkillsConfigurationRequest): Observable<SkillsConfiguration> {
    return this._put(SKILLS_CONFIGURATION_ENDPOINT, { skills_configuration: request }).pipe(
      map((res) => this._handleResponse(res, 'skills_configuration')),
      catchError((res) => this._handleError(res)),
    );
  }

  listFrameworkNodes(
    frameworkId: number,
    params: {
      path_ids?: number[];
      page?: number;
      page_size?: number;
      skill_framework_version_id?: number;
    } = {},
  ): Observable<SkillFrameworkNodesData> {
    const queryString = this._buildQueryString(params);
    const base = `${FRAMEWORKS_ENDPOINT}/${frameworkId}/nodes`;
    const url = queryString ? `${base}?${queryString}` : base;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  getSkillDetail(skill: SelectedSkill, skillFrameworkVersionId?: number): Observable<SkillDetail> {
    const queryString = this._buildQueryString({
      path_ids: skill.path_ids,
      skill_framework_version_id: skillFrameworkVersionId,
    });
    const base = `${SKILLS_ENDPOINT}/${skill.id}`;
    const url = queryString ? `${base}?${queryString}` : base;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res, 'skill')),
      catchError((res) => this._handleError(res)),
    );
  }

  searchSkills(frameworkId: number, params: SkillSearchParams): Observable<SkillSearchData> {
    const url = `${FRAMEWORKS_ENDPOINT}/${frameworkId}/skills/search?${this._buildQueryString(params)}`;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }
}
